/* assets/dtf-pdp-tracking.js
 *
 * GA4 click tracking for the DTF by-size PDP (/products/dtf-transfers) — both the
 * PRE-UPLOAD state (upload-box tabs, image carousel, video, price calculator,
 * below-fold accordions) and the POST-UPLOAD INLINE single-upload state (size
 * tabs, design-note, pre-cut, add-another-design, proceed to checkout).
 *
 * Dispatch mirrors the sibling trackers (assets/multiupload-ga-tracking.js,
 * dtf-cta-tracking.js, avatar-tracking.js): gtag('event', name, params) guarded by
 * typeof gtag, with environment ('production'/'staging' via Shopify.theme.role ===
 * 'main') + device_type on every event. All events are prefixed dtf_pdp_ so they
 * isolate cleanly from multiupload_* in GA4.
 *
 * ARCHITECTURE: one self-attaching asset, document-level CAPTURE-phase delegation
 * (negligible main-thread cost on this ~48K-node page; survives the theme's
 * stopImmediatePropagation() + dynamic DOM injection). No per-element listeners.
 *
 * ⚠️ COLLISION GUARD (critical): the post-upload inline controls share CSS classes
 * with the multiupload MODAL (instrumented separately by multiupload-ga-tracking.js,
 * which scopes itself to closest('multi-upload-wrapper')). The inline single-upload
 * controls live under <upload-controls>; the modal lives under <multi-upload-wrapper>
 * — disjoint containers (recon-confirmed via the rendered PDP + bySize_popular.js
 * single-vs-multi selectors). Every post-upload listener requires inlineScope():
 * inside <upload-controls> AND NOT inside <multi-upload-wrapper>. Result: a given
 * click fires EITHER a dtf_pdp_* event (inline) OR a multiupload_* event (modal),
 * never both. The two trackers' change-listeners both sit on document(capture); the
 * scope checks are mutually exclusive, so exactly one fires.
 *
 * Selectors below are recon-confirmed against the rendered sandbox PDP (2026-06-17).
 */
(function () {
  'use strict';

  if (window.__dtfPdpGATracking) return;
  window.__dtfPdpGATracking = true;

  /* ----------------------------- dispatch ----------------------------- */

  function environment() {
    return (window.Shopify && Shopify.theme && Shopify.theme.role === 'main')
      ? 'production'
      : 'staging';
  }

  function deviceType() {
    return window.innerWidth <= 767 ? 'mobile' : 'desktop';
  }

  // dtf vs uv PDP (uv-dtf-stickers-decals-by-size = suffix uv-by-size-middle-scroll). The
  // AI Edit Tools dropdown renders on both; product_type keeps them separable in GA4.
  function productType() {
    return /(^|\s)template-product-uv-by-size-middle-scroll(\s|$)/.test(document.body.className) ? 'uv' : 'dtf';
  }

  function fireGAEvent(name, params) {
    if (typeof gtag === 'undefined') return;
    params = params || {};
    params.environment = environment();
    params.device_type = deviceType();
    gtag('event', name, params);
  }

  /* COLLISION GUARD — inline single-upload context only, never the modal. */
  function inlineScope(el) {
    return !!(el.closest && el.closest('upload-controls') && !el.closest('multi-upload-wrapper'));
  }

  /* ------------------------------ state ------------------------------- */

  var carouselFired = false; // dtf_pdp_carousel_interact fires once per page load

  var UPLOAD_TAB_MAP = {
    upload: 'upload',
    recent: 'previous_designs',
    ai: 'create_ai',
    create: 'design_studio'
  };

  // AI Edit Tools dropdown (nt-dtf-3col.js): data-nt-aied value -> GA tool param.
  var AIED_TOOL_MAP = { edit: 'edit_with_prompt', recolor: 'recolor_image', effects: 'image_effects' };

  // Map a below-fold accordion <details> to its section value via the .accordion__title text.
  function accordionSection(details) {
    var titleEl = details.querySelector ? details.querySelector('.accordion__title') : null;
    var t = (titleEl ? titleEl.textContent : '').toLowerCase();
    if (t.indexOf('pressing') >= 0) return 'pressing';
    if (t.indexOf('art upload') >= 0 || t.indexOf('art rec') >= 0) return 'art_recs';
    if (t.indexOf('shipping') >= 0) return 'shipping';
    if (t.indexOf('satisfaction') >= 0 || t.indexOf('guarantee') >= 0) return 'guarantee';
    if (t.indexOf('feature') >= 0) return 'features';
    return null; // not one of the 5 tracked sections — ignore
  }

  /* ------------------------------ clicks ------------------------------ */

  document.addEventListener('click', function (e) {
    var t = e.target;
    if (!t || !t.closest) return;

    /* --- PRE-UPLOAD / page-level (not collision-scoped) --- */

    // Upload-box tabs: Upload / Previous Designs / Create with AI / Design Studio
    var tab = t.closest('[data-dtfut-tab]');
    if (tab) {
      var key = tab.getAttribute('data-dtfut-tab');
      fireGAEvent('dtf_pdp_upload_tab_click', { tab: UPLOAD_TAB_MAP[key] || key });
      return;
    }

    // Main image carousel — arrows + thumbnail/dot strip. FIRE ONCE per page load.
    // (Swipe is not covered — arrow + thumbnail clicks only, per spec.)
    if (!carouselFired && (t.closest('.arrow-key') || t.closest('.product-gallery__thumbnails'))) {
      carouselFired = true;
      fireGAEvent('dtf_pdp_carousel_interact', {});
      return;
    }

    // Video play — the single <video-popup> play link (real <video> is lazy in a <template>)
    if (t.closest('[data-js-video-popup-link], .video-popup__link')) {
      fireGAEvent('dtf_pdp_video_play', {});
      return;
    }

    // Price Calculator trigger (below the fold). ._price-calc-link is the
    // class the trigger link actually carries on BOTH arms —
    // .openPriceCalculator matches nothing on either PDP (dead selector,
    // kept for any older template still using it), so this event recorded
    // zero everywhere until 2026-07-21.
    if (t.closest('.openPriceCalculator, ._price-calc-link')) {
      fireGAEvent('dtf_pdp_price_calculator_click', {});
      return;
    }

    // AI Edit Tools dropdown ITEM (nt-dtf-3col.js: Edit with Prompt / Recolor Image /
    // Image Effects). ⚠ The open menu PORTALS to <body>, so the clicked .nt-aied-item is
    // no longer inside <upload-controls> — inlineScope(t) would fail. Scope off the
    // still-in-place OPEN host instead (this capture listener runs before nt-dtf-3col's
    // BUBBLE handler calls closeAll(), so .nt-aied-open is still set at click time).
    // inlineScope(host) keeps this to the single-upload inline surface (host in
    // <upload-controls>, not <multi-upload-wrapper>); the builder-tile host lives in
    // <multi-upload-wrapper> and is handled by multiupload-ga-tracking.js — mutually
    // exclusive, so exactly one tracker fires per click. Must sit BEFORE the inlineScope
    // gate below (the portaled item can't pass it).
    var aiedItem = t.closest('.nt-aied-item');
    if (aiedItem) {
      var aiedHost = document.querySelector('.nt-aied-host.nt-aied-open');
      if (aiedHost && inlineScope(aiedHost)) {
        var tool = AIED_TOOL_MAP[aiedItem.getAttribute('data-nt-aied')];
        if (tool) fireGAEvent('dtf_pdp_ai_edit_tool_click', { tool: tool, product_type: productType() });
      }
      return;
    }

    /* --- POST-UPLOAD INLINE (collision-scoped to <upload-controls>) --- */
    if (!inlineScope(t)) return;

    // AI Edit Tools dropdown TRIGGER open (nt-dtf-3col.js). The trigger stays in place
    // inside <upload-controls>, so inlineScope(t) already gated us here. Fire only when
    // this click OPENS the menu — at capture the host is not yet .nt-aied-open
    // (nt-dtf-3col toggles it in its later bubble handler), so a close never fires an open.
    var aiedTrg = t.closest('.nt-aied-trigger');
    if (aiedTrg) {
      var trgHost = aiedTrg.closest('.nt-aied-host');
      if (trgHost && !trgHost.classList.contains('nt-aied-open')) {
        fireGAEvent('dtf_pdp_ai_edit_tools_open', { product_type: productType() });
      }
      return;
    }

    // "Add another design" box. The previous/AI links live INSIDE .secondDropZone,
    // so check them first and return before the browse/drop-area fallback.
    if (t.closest('.create_with_ai_link')) {
      fireGAEvent('dtf_pdp_add_design_ai_click', {});
      return;
    }
    if (t.closest('.purchase_from_previous')) {
      fireGAEvent('dtf_pdp_add_design_previous_click', {});
      return;
    }
    if (t.closest('.secondDropZone')) {
      fireGAEvent('dtf_pdp_add_design_upload_click', {});
      return;
    }

    // "Add a note for our designers" expander
    if (t.closest('.nt-design-note__header')) {
      fireGAEvent('dtf_pdp_note_toggle', {});
      return;
    }

    // Proceed to Checkout (inline) — .addToCartGroupItems is a distinct class token
    // from the modal's .addToCartGroupItemsMultiple (no substring match), plus inlineScope.
    if (t.closest('.addToCartGroupItems')) {
      fireGAEvent('dtf_pdp_proceed_checkout', {});
      return;
    }
  }, true);

  /* ----------- change (capture): size tabs + pre-cut (inline only) ----------- */

  document.addEventListener('change', function (e) {
    var t = e.target;
    if (!t || !t.closest || !inlineScope(t)) return;
    var cl = t.classList;

    // Custom Size <-> Popular Size (hidden radios .tabOptionName, value custom/popular)
    if (cl && cl.contains('tabOptionName')) {
      if (t.checked) fireGAEvent('dtf_pdp_size_tab_switch', { tab: t.value });
      return;
    }

    // "Pre-cut my transfers" checkbox
    if (t.id === 'preCutOption') {
      fireGAEvent('dtf_pdp_precut_toggle', {});
      return;
    }
  }, true);

  /* ----------- toggle (capture): below-fold accordions, OPEN only -----------
   * Native <details>/<summary> (Shopify collapsible_row). The 'toggle' event does
   * not bubble, but capture-phase document listeners still receive it. Fire only
   * when the section EXPANDS (details.open === true); closing fires nothing. */

  document.addEventListener('toggle', function (e) {
    var d = e.target;
    if (!d || d.tagName !== 'DETAILS' || !d.open) return;
    var section = accordionSection(d);
    if (section) fireGAEvent('dtf_pdp_accordion_open', { section: section });
  }, true);
})();
